import React from 'react';
import { sortBy } from 'ramda';
import ArrowUpIcon from '@moda/icons/arrow-up-12';
import { JEWELRY_CATEGORIES } from '../../constants';
import { usePLPParams } from '../../hooks/usePLPParams';
import { useCategoryTree, CategoryTree } from '../../hooks/useCategoryTree';
import { Rotate } from '../Rotate';
import { PageFilterLink } from './PageFilterLink';
import { PageFilterPanel } from './PageFilterPanel';

import './PageFilterCategory.scss';

type Props = {
  categoryTree: CategoryTree | null;
  mobile?: boolean;
  showCounts?: boolean;
  onClose: () => void;
};

export const PageFilterJewelryCategory: React.FC<Props> = ({
  categoryTree,
  mobile,
  showCounts,
  onClose
}) => {
  const {
    params: { category }
  } = usePLPParams();

  const { selectedCategoryNode, activeCategoryNode, getCategoryNodeHref, getVisibleChildren } =
    useCategoryTree(categoryTree);

  const jewelryCategoryNodes = categoryTree?.children
    ?.find(child => child?.slug === 'women')
    ?.children?.filter(child => child?.slug && JEWELRY_CATEGORIES.includes(child.slug));

  const topLevelCategoryNode = jewelryCategoryNodes?.find(node => node?.slug === 'jewelry');

  const jewelryCategoryAndSubcategoryNodes = sortBy(
    node => (node?.slug === 'jewelry' ? Infinity : node?.name ?? ''),
    [...(jewelryCategoryNodes ?? []), ...(topLevelCategoryNode?.children ?? [])]
  );

  const isTopLevel = category === 'jewelry';

  const title =
    category === 'jewelry' ? (isTopLevel ? 'Jewelry' : 'All Jewelry') : activeCategoryNode?.name;

  const categories = isTopLevel
    ? jewelryCategoryAndSubcategoryNodes
    : getVisibleChildren(activeCategoryNode);

  const content = (
    <>
      {!mobile && <div className="PageFilterCategory__title">{title}</div>}

      {(categories || []).map(
        category =>
          category &&
          category.slug &&
          category.slug !== 'sale' && (
            <PageFilterLink
              selected={selectedCategoryNode === category}
              key={category.slug}
              to={getCategoryNodeHref(category) || ''}
              onClick={onClose}
            >
              <span>{category.slug === 'jewelry' ? 'All Jewelry' : category.name}</span>

              {showCounts && <span>{category.totalItems}</span>}
            </PageFilterLink>
          )
      )}
    </>
  );

  if (mobile) {
    return (
      <PageFilterPanel onClose={onClose} title="Category">
        {content}
      </PageFilterPanel>
    );
  }

  return (
    <div className="PageFilterCategory">
      {!isTopLevel && (
        <PageFilterLink
          aria-label="Go Back to Jewelry"
          back
          to={topLevelCategoryNode ? getCategoryNodeHref(topLevelCategoryNode) || '' : ''}
        >
          <Rotate degrees={-90}>
            <ArrowUpIcon />
          </Rotate>
          Jewelry
        </PageFilterLink>
      )}

      {content}
    </div>
  );
};
